import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import toast from 'react-hot-toast';
import api from '../../config/api';

const UserManagementContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
`;

const SearchInput = styled.input`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  font-size: 0.9rem;
  min-width: 300px;
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 2px ${theme.colors.primary.light}20;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background: ${theme.colors.primary.main};
  color: white;

  &:hover:not(:disabled) {
    background: ${theme.colors.primary.dark};
    transform: translateY(-1px);
  }
`;

const SecondaryButton = styled(Button)`
  background: ${theme.colors.background.secondary};
  color: ${theme.colors.text.primary};
  border: 1px solid ${theme.colors.border.primary};

  &:hover:not(:disabled) {
    background: ${theme.colors.background.primary};
    border-color: ${theme.colors.primary.main};
  }
`;

// Styled button components - kept for future use
// const DangerButton = styled(Button)`
//   background: ${theme.colors.error.main};
//   color: white;

//   &:hover:not(:disabled) {
//     background: ${theme.colors.error.dark};
//     transform: translateY(-1px);
//   }
// `;

// const WarningButton = styled(Button)`
//   background: ${theme.colors.warning.main};
//   color: white;

//   &:hover:not(:disabled) {
//     background: ${theme.colors.warning.dark};
//     transform: translateY(-1px);
//   }
// `;

const UsersTable = styled.div`
  background: ${theme.colors.background.primary};
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  border: 1px solid ${theme.colors.border.primary};
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 1fr;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  background: ${theme.colors.background.secondary};
  border-bottom: 1px solid ${theme.colors.border.primary};
  font-weight: 600;
  font-size: 0.9rem;
  color: ${theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 1fr;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.border.secondary};
  align-items: center;
  transition: background-color 0.2s ease;

  &:hover {
    background: ${theme.colors.background.secondary};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const UserName = styled.div`
  font-weight: 600;
  color: ${theme.colors.text.primary};
`;

const UserEmail = styled.div`
  font-size: 0.85rem;
  color: ${theme.colors.text.secondary};
`;

const StatusBadge = styled.span`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ActiveBadge = styled(StatusBadge)`
  background: ${theme.colors.success.light};
  color: ${theme.colors.success.dark};
`;

const InactiveBadge = styled(StatusBadge)`
  background: ${theme.colors.error.light};
  color: ${theme.colors.error.dark};
`;

const PendingBadge = styled(StatusBadge)`
  background: ${theme.colors.warning.light};
  color: ${theme.colors.warning.dark};
`;

const RoleBadge = styled.span`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  font-size: 0.8rem;
  font-weight: 500;
  background: ${theme.colors.primary.light};
  color: ${theme.colors.primary.dark};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};
  flex-wrap: wrap;
`;

const SmallButton = styled(Button)`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  font-size: 0.8rem;
  min-width: 60px;
  white-space: nowrap;
  text-align: center;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  margin: 0 0 ${theme.spacing.lg} 0;
  color: ${theme.colors.text.primary};
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing.lg};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${theme.spacing.sm};
  font-weight: 500;
  color: ${theme.colors.text.primary};
`;

const Input = styled.input`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  font-size: 0.9rem;
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 2px ${theme.colors.primary.light}20;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  font-size: 0.9rem;
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 2px ${theme.colors.primary.light}20;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  font-size: 0.9rem;
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 2px ${theme.colors.primary.light}20;
  }
`;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    user_type: 'personal',
    role: 'user',
    academy_enabled: false
  });

  // Form states
  const [createForm, setCreateForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    user_type: 'personal',
    role: 'user',
    password: ''
  });

  const [inviteForm, setInviteForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    user_type: 'personal',
    role: 'user',
    message: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/users');
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/admin/users', createForm);

      toast.success('User created successfully');
      setShowCreateModal(false);
      setCreateForm({
        email: '',
        first_name: '',
        last_name: '',
        user_type: 'personal',
        role: 'user',
        password: ''
      });
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.message || 'Failed to create user');
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/admin/users/invite', inviteForm);

      toast.success('Invitation sent successfully');
      setShowInviteModal(false);
      setInviteForm({
        email: '',
        first_name: '',
        last_name: '',
        user_type: 'personal',
        role: 'user',
        message: ''
      });
    } catch (error) {
      console.error('Error inviting user:', error);
      toast.error(error.message || 'Failed to send invitation');
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      await api.put(`/api/admin/users/${userId}/toggle-status`, { is_active: !currentStatus });

      toast.success(`User ${currentStatus ? 'disabled' : 'activated'} successfully`);
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error(error.message || 'Failed to update user status');
    }
  };

  const handleManualActivate = async (userId) => {
    try {
      await api.post(`/api/admin/users/${userId}/activate`);
      toast.success('User activated — they can log in now');
      fetchUsers();
    } catch (error) {
      console.error('Error activating user:', error);
      toast.error(error.response?.data?.error || 'Failed to activate user');
    }
  };

  const handleDeleteUser = async (userId) => {
    // Show confirmation toast with action buttons
    toast((t) => (
      <div className="flex flex-col space-y-2">
        <span className="font-medium">Delete User</span>
        <span className="text-sm text-gray-600">This action cannot be undone.</span>
        <div className="flex space-x-2">
          <button
            onClick={() => {
              toast.dismiss(t.id);
              performDeleteUser(userId);
            }}
            className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
          >
            Delete
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </div>
    ), { duration: 10000 });
    return;
  };

  const performDeleteUser = async (userId) => {

    try {
      await api.delete(`/api/admin/users/${userId}`);

      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.message || 'Failed to delete user');
    }
  };

  const handleSendPasswordReset = async (userId) => {
    try {
      await api.post(`/api/admin/users/${userId}/send-password-reset`);

      toast.success('Password reset email sent');
    } catch (error) {
      console.error('Error sending password reset:', error);
      toast.error(error.message || 'Failed to send password reset');
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/api/admin/users/${selectedUser.id}`, editForm);

      toast.success('User updated successfully');
      setShowUserDetails(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.message || 'Failed to update user');
    }
  };

  const handleResendInvite = async (userId) => {
    try {
      await api.post(`/api/admin/users/${userId}/resend-invite`);
      toast.success('Invitation re-sent');
    } catch (error) {
      console.error('Error resending invite:', error);
      const msg = error.response?.data?.error || error.message || 'Failed to resend invite';
      toast.error(msg);
    }
  };

  const handleGetActivationLink = async (userId) => {
    try {
      const res = await api.get(`/api/admin/users/${userId}/activation-link`);
      const url = res.data?.activation_url;
      if (!url) {
        toast.error('No activation link returned');
        return;
      }
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Activation link copied to clipboard');
      } catch {
        // Fallback: show prompt for manual copy
        window.prompt('Activation link (copy manually):', url);
      }
    } catch (error) {
      console.error('Error getting activation link:', error);
      const msg = error.response?.data?.error || error.message || 'Failed to get activation link';
      toast.error(msg);
    }
  };

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.last_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (user) => {
    if (!user.email_verified) {
      return <PendingBadge>Pending</PendingBadge>;
    }
    if (!user.is_active) {
      return <InactiveBadge>Inactive</InactiveBadge>;
    }
    return <ActiveBadge>Active</ActiveBadge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <UserManagementContainer>
      <ActionBar>
        <SearchInput
          type="text"
          placeholder="Search users by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <ButtonGroup>
          <PrimaryButton 
            onClick={() => setShowInviteModal(true)}
            title="Send invitation email to new user"
            aria-label="Send invitation email to new user"
          >
            Invite User
          </PrimaryButton>
          <SecondaryButton 
            onClick={() => setShowCreateModal(true)}
            title="Create new user account directly"
            aria-label="Create new user account directly"
          >
            Create User
          </SecondaryButton>
          <SecondaryButton 
            onClick={fetchUsers} 
            disabled={loading}
            title="Refresh user list"
            aria-label="Refresh user list"
          >
            Refresh
          </SecondaryButton>
        </ButtonGroup>
      </ActionBar>

      <UsersTable>
        <TableHeader>
          <div>User</div>
          <div>Role</div>
          <div>Status</div>
          <div>Type</div>
          <div>Last Login</div>
          <div>Created</div>
          <div>Actions</div>
        </TableHeader>

        {loading ? (
          <TableRow>
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem' }}>
              Loading users...
            </div>
          </TableRow>
        ) : filteredUsers.length === 0 ? (
          <TableRow>
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem' }}>
              No users found
            </div>
          </TableRow>
        ) : (
          filteredUsers.map((user) => (
            <TableRow key={user.id}>
              <UserInfo>
                <UserName>{user.first_name} {user.last_name}</UserName>
                <UserEmail>{user.email}</UserEmail>
              </UserInfo>
              <div>
                <RoleBadge>{user.role}</RoleBadge>
              </div>
              <div>
                {getStatusBadge(user)}
              </div>
              <div>{user.user_type}</div>
              <div>{user.last_login ? formatDate(user.last_login) : 'Never'}</div>
              <div>{formatDate(user.created_at)}</div>
              <ActionButtons>
                <SmallButton 
                  onClick={() => {
                    setSelectedUser(user);
                    setEditForm({
                      first_name: user.first_name,
                      last_name: user.last_name,
                      email: user.email,
                      user_type: user.user_type,
                      role: user.role,
                      academy_enabled: !!user.academy_enabled
                    });
                    setShowUserDetails(true);
                  }}
                  title="Edit User Details"
                  aria-label="Edit User Details"
                >
                  Edit
                </SmallButton>
                {(!user.is_active || !user.email_verified) && (
                  <SmallButton
                    onClick={() => handleManualActivate(user.id)}
                    title="Activate immediately (no email link required)"
                    aria-label="Activate user manually"
                    style={{ fontWeight: 600 }}
                  >
                    Activate now
                  </SmallButton>
                )}
                <SmallButton 
                  onClick={() => handleToggleUserStatus(user.id, user.is_active)}
                  title={user.is_active ? 'Disable User' : 'Activate User'}
                  aria-label={user.is_active ? 'Disable User' : 'Activate User'}
                >
                  {user.is_active ? 'Disable' : 'Activate'}
                </SmallButton>
                <SmallButton 
                  onClick={() => handleSendPasswordReset(user.id)}
                  title="Send Password Reset Email"
                  aria-label="Send Password Reset Email"
                >
                  Reset Password
                </SmallButton>
                <SmallButton 
                  onClick={() => handleDeleteUser(user.id)}
                  title="Delete User (Cannot be undone)"
                  aria-label="Delete User (Cannot be undone)"
                >
                  Delete
                </SmallButton>
                {(!user.email_verified || !user.is_active) && (
                  <>
                    <SmallButton
                      onClick={() => handleResendInvite(user.id)}
                      title="Resend Invite Email"
                      aria-label="Resend Invite Email"
                    >
                      Resend Invite
                    </SmallButton>
                    <SmallButton
                      onClick={() => handleGetActivationLink(user.id)}
                      title="Copy Activation Link"
                      aria-label="Copy Activation Link"
                    >
                      Get Link
                    </SmallButton>
                  </>
                )}
              </ActionButtons>
            </TableRow>
          ))
        )}
      </UsersTable>

      {/* Create User Modal */}
      {showCreateModal && (
        <Modal onClick={() => setShowCreateModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Create New User</ModalTitle>
            <form onSubmit={handleCreateUser}>
              <FormGroup>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>First Name *</Label>
                <Input
                  type="text"
                  value={createForm.first_name}
                  onChange={(e) => setCreateForm({...createForm, first_name: e.target.value})}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Last Name *</Label>
                <Input
                  type="text"
                  value={createForm.last_name}
                  onChange={(e) => setCreateForm({...createForm, last_name: e.target.value})}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>User Type</Label>
                <Select
                  value={createForm.user_type}
                  onChange={(e) => setCreateForm({...createForm, user_type: e.target.value})}
                >
                  <option value="personal">Personal</option>
                  <option value="marina">Marina</option>
                  <option value="fleet">Fleet</option>
                  <option value="professional">Professional</option>
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Role</Label>
                <Select
                  value={createForm.role}
                  onChange={(e) => setCreateForm({...createForm, role: e.target.value})}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({...createForm, password: e.target.value})}
                  required
                />
              </FormGroup>
              <ButtonGroup>
                <PrimaryButton type="submit">Create User</PrimaryButton>
                <SecondaryButton type="button" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </SecondaryButton>
              </ButtonGroup>
            </form>
          </ModalContent>
        </Modal>
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <Modal onClick={() => setShowInviteModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Invite New User</ModalTitle>
            <form onSubmit={handleInviteUser}>
              <FormGroup>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>First Name *</Label>
                <Input
                  type="text"
                  value={inviteForm.first_name}
                  onChange={(e) => setInviteForm({...inviteForm, first_name: e.target.value})}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Last Name *</Label>
                <Input
                  type="text"
                  value={inviteForm.last_name}
                  onChange={(e) => setInviteForm({...inviteForm, last_name: e.target.value})}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>User Type</Label>
                <Select
                  value={inviteForm.user_type}
                  onChange={(e) => setInviteForm({...inviteForm, user_type: e.target.value})}
                >
                  <option value="personal">Personal</option>
                  <option value="marina">Marina</option>
                  <option value="fleet">Fleet</option>
                  <option value="professional">Professional</option>
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Role</Label>
                <Select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({...inviteForm, role: e.target.value})}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Personal Message (Optional)</Label>
                <TextArea
                  value={inviteForm.message}
                  onChange={(e) => setInviteForm({...inviteForm, message: e.target.value})}
                  placeholder="Add a personal message to the invitation..."
                />
              </FormGroup>
              <ButtonGroup>
                <PrimaryButton type="submit">Send Invitation</PrimaryButton>
                <SecondaryButton type="button" onClick={() => setShowInviteModal(false)}>
                  Cancel
                </SecondaryButton>
              </ButtonGroup>
            </form>
          </ModalContent>
        </Modal>
      )}

      {/* Edit User Modal */}
      {showUserDetails && selectedUser && (
        <Modal onClick={() => setShowUserDetails(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Edit User</ModalTitle>
            <form onSubmit={handleEditUser}>
              <FormGroup>
                <Label>First Name *</Label>
                <Input
                  type="text"
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Last Name *</Label>
                <Input
                  type="text"
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>User Type *</Label>
                <Select
                  value={editForm.user_type}
                  onChange={(e) => setEditForm({ ...editForm, user_type: e.target.value })}
                >
                  <option value="personal">Personal</option>
                  <option value="marina">Marina</option>
                  <option value="fleet">Fleet</option>
                  <option value="professional">Professional</option>
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Role *</Label>
                <Select
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>
                  <input
                    type="checkbox"
                    checked={!!editForm.academy_enabled}
                    onChange={(e) => setEditForm({ ...editForm, academy_enabled: e.target.checked })}
                    style={{ marginRight: '0.5rem' }}
                  />
                  BoatWise Academy access
                </Label>
              </FormGroup>
              <ButtonGroup>
                <PrimaryButton type="submit">Update User</PrimaryButton>
                <SecondaryButton type="button" onClick={() => setShowUserDetails(false)}>
                  Cancel
                </SecondaryButton>
              </ButtonGroup>
            </form>
          </ModalContent>
        </Modal>
      )}
    </UserManagementContainer>
  );
};

export default UserManagement;
